import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import { useStateContext } from "contexts/ContextProvider";
import CardDashboardText from "components/CardDashboardText";
// MUI components
import Grid from "@mui/material/Grid2";
import Typography from "@mui/material/Typography";
import { DataGrid } from "@mui/x-data-grid";
import { Box, useTheme, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import CardDashboardItem from "components/CardDashboardItem";
import {
  formatDateLongNoYear,
  getStatusColor,
} from "helpers/ServiceHelpers";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import api from "services/api";
import ColumnFilter from "components/ColumnFilter";
import ColumnDateFilter from "components/ColumnDateFilter";
import DataGridHeader from "components/DataGridHeader";
import useColumnFilters from "hooks/useColumnFilters";
import useAssignmentUpdates from "hooks/useAssignmentUpdates";
import { usePresence } from "contexts/PresenceContext";
import { usePagePresence } from "hooks/useAssignmentPresence";
import ClearIcon from "@mui/icons-material/ClearRounded";
import { GridToolbarContainer } from "@mui/x-data-grid";
import CEQuickFilter from "components/CEQuickFilter";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import AddTaskRoundedIcon from "@mui/icons-material/AddTaskRounded";
import TaskRoundedIcon from "@mui/icons-material/TaskRounded";
import CallRoundedIcon from "@mui/icons-material/CallRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import { OVERLAY_NAMES } from "constants/Overlays";
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import { HtmlTooltip } from "components/HeatMap";

dayjs.extend(isoWeek);

const DashboardInterpretationsWeekly = ({ user }) => {
  const {
    refreshPage,
    showSnackbar,
    handleErrorDialog,
    setOverlayData,
    setOverlayName,
  } = useStateContext();
  const theme = useTheme();
  const navigate = useNavigate();

  // Track last update for snackbar notification
  const [lastUpdate, setLastUpdate] = useState(null);

  // Set up entity tracking for InterpretationsList to receive real-time updates
  const { setCurrentEntity } = usePresence();
  usePagePresence();

  useEffect(() => {
    // Set entity to "InterpretationsList:all" so socket joins the entity room for list-level updates
    setCurrentEntity({
      entityType: "InterpretationsList",
      entityId: "all",
    });

    return () => {
      // Clean up entity tracking when component unmounts
      setCurrentEntity({ entityType: null, entityId: null });
    };
  }, [setCurrentEntity]);

  const [isLoading, setIsLoading] = useState(true);

  const [listOfServices] = useState([]);
  const [listOfClients, setListOfClients] = useState([]);
  const [listOfLinguists, setListOfLinguists] = useState([]);
  const [listOfLanguages, setListOfLanguages] = useState([]);
  const [listOfStatuses, setListOfStatuses] = useState([]);
  const [listOfTypes, setListOfTypes] = useState([]);

  const [todaysAssignments, setTodaysAssignments] = useState([]);
  const [tomorrowsAssignments, setTomorrowsAssignments] = useState([]);
  const [thisWeeksAssignments, setThisWeeksAssignments] = useState([]);
  const [nextWeeksAssignments, setNextWeeksAssignments] = useState([]);
  const [monthTilYesterdayAssignments, setMonthTilYesterdayAssignments] =
    useState([]);

  const [assignmentsCounts, setAssignmentsCounts] = useState();

  const [openTodaysFilter, setOpenTodaysFilter] = useState(null);
  const [openTomorrowsFilter, setOpenTomorrowsFilter] = useState(null);
  const [openThisWeeksFilter, setOpenThisWeeksFilter] = useState(null);
  const [openNextWeeksFilter, setOpenNextWeeksFilter] = useState(null);
  const [openMonthTilYesterdayFilter, setOpenMonthTilYesterdayFilter] =
    useState(null);
  const [todaysFilterModel, setTodaysFilterModel] = useState({ items: [] });
  const [tomorrowsFilterModel, setTomorrowsFilterModel] = useState({
    items: [],
  });
  const [thisWeeksFilterModel, setThisWeeksFilterModel] = useState({
    items: [],
  });
  const [nextWeeksFilterModel, setNextWeeksFilterModel] = useState({
    items: [],
  });
  const [monthTilYesterdayFilterModel, setMonthTilYesterdayFilterModel] =
    useState({ items: [] });

  const [contextMenu, setContextMenu] = useState(null);
  const [selectedRow, setSelectedRow] = React.useState();

  // Wrapper functions to close all other filters when one opens
  const handleSetOpenTodaysFilter = (value) => {
    if (value) {
      setOpenTomorrowsFilter(null);
      setOpenThisWeeksFilter(null);
      setOpenNextWeeksFilter(null);
      setOpenMonthTilYesterdayFilter(null);
    }
    setOpenTodaysFilter(value);
  };

  const handleSetOpenTomorrowsFilter = (value) => {
    if (value) {
      setOpenTodaysFilter(null);
      setOpenThisWeeksFilter(null);
      setOpenNextWeeksFilter(null);
      setOpenMonthTilYesterdayFilter(null);
    }
    setOpenTomorrowsFilter(value);
  };

  const handleSetOpenThisWeeksFilter = (value) => {
    if (value) {
      setOpenTodaysFilter(null);
      setOpenTomorrowsFilter(null);
      setOpenNextWeeksFilter(null);
      setOpenMonthTilYesterdayFilter(null);
    }
    setOpenThisWeeksFilter(value);
  };

  const handleSetOpenNextWeeksFilter = (value) => {
    if (value) {
      setOpenTodaysFilter(null);
      setOpenTomorrowsFilter(null);
      setOpenThisWeeksFilter(null);
      setOpenMonthTilYesterdayFilter(null);
    }
    setOpenNextWeeksFilter(value);
  };

  const handleSetOpenMonthTilYesterdayFilter = (value) => {
    if (value) {
      setOpenTodaysFilter(null);
      setOpenTomorrowsFilter(null);
      setOpenThisWeeksFilter(null);
      setOpenNextWeeksFilter(null);
    }
    setOpenMonthTilYesterdayFilter(value);
  };

  // Wrapped clear handlers that also clear quick filters
  const handleClearTodaysFilters = () => {
    clearTodaysFilters();
    setTodaysFilterModel({ items: [] });
  };

  const handleClearTomorrowsFilters = () => {
    clearTomorrowsFilters();
    setTomorrowsFilterModel({ items: [] });
  };

  const handleClearThisWeeksFilters = () => {
    clearThisWeeksFilters();
    setThisWeeksFilterModel({ items: [] });
  };

  const handleClearNextWeeksFilters = () => {
    clearNextWeeksFilters();
    setNextWeeksFilterModel({ items: [] });
  };

  const handleClearMonthTilYesterdayFilters = () => {
    clearMonthTilYesterdayFilters();
    setMonthTilYesterdayFilterModel({ items: [] });
  };

  const handleContextMenu = (event) => {
    event.preventDefault();

    setSelectedRow(Number(event.currentTarget.getAttribute("data-id")));
    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
          }
        : // repeated contextmenu when it is already open closes it with Chrome 84 on Ubuntu
          // Other native context menus might behave different.
          // With this behavior we prevent contextmenu from the backdrop to re-locale existing context menus.
          null,
    );
  };

  const handleContextMenuEdit = () => {
    if (!selectedRow) {
      console.error("No selected row for edit action");
      showSnackbar("Please select an assignment to edit", theme, "error");
      return;
    }

    api
      .get(`/services/interpretations/${selectedRow}`)
      .then((response) => {
        if (!response?.data) {
          throw new Error("No interpretation data received");
        }

        const interpretationData = response.data;
        
        // Validate required data exists
        if (!interpretationData.id) {
          throw new Error("Invalid interpretation data: missing ID");
        }

        const secondData = {
          ...interpretationData,
          Action: OVERLAY_NAMES.INTERPRETATION_ACTION_EDIT,
          Interpretations: listOfServices || [],
          Clients: listOfClients || [],
          Linguists: listOfLinguists || [],
          Languages: listOfLanguages || [],
          InterpretationStatuses: listOfStatuses || [],
          InterpretationTypes: listOfTypes || [],
        };

        setOverlayData(secondData);
        setOverlayName(OVERLAY_NAMES.INTERPRETATION_NEW_EDIT);
        setContextMenu(null);
      })
      .catch((error) => {
        console.error("Error fetching interpretation for edit:", error);
        const message =
          error.response?.data?.message || error.message || "Failed to load interpretation details.";
        handleErrorDialog(message, theme);
        showSnackbar(message, theme, "error");
      });
  };

  const handleContextMenuAddTask = () => {
    if (!selectedRow) {
      console.error("No selected row for add task action");
      showSnackbar("Please select an assignment to add a task", theme, "error");
      return;
    }

    const interpretationData = listOfServices.find(
      ({ id }) => id === selectedRow,
    );

    if (!interpretationData) {
      console.error("No interpretation data found for selected row:", selectedRow);
      showSnackbar("Assignment details not found. Please try again.", theme, "error");
      return;
    }

    // Validate required fields
    if (!interpretationData.id || !interpretationData.ProjectNumber) {
      console.error("Invalid interpretation data:", interpretationData);
      showSnackbar("Invalid assignment data. Please try again.", theme, "error");
      return;
    }

    setOverlayData({
      Action: OVERLAY_NAMES.TASK_ACTION_NEW,
      Task: {
        EntityType: "Service",
        EntityID: selectedRow,
        ServiceType: "Interpretation",
      },
      Service: {
        id: interpretationData.id,
        ProjectNumber: interpretationData.ProjectNumber,
        Details: interpretationData.Details || "",
        ServiceCategory: interpretationData.ServiceCategory || "Interpretation",
      },
    });
    setOverlayName(OVERLAY_NAMES.TASK_NEW_EDIT);
    setContextMenu(null);
  };

  const handleContextMenuComplete = () => {
    api
      .get(`/services/interpretations/${selectedRow}`)
      .then((response) => {
        const interpretationData = response.data;

        const secondData = {
          ...interpretationData,
          Action: OVERLAY_NAMES.INTERPRETATION_ACTION_COMPLETE,
          Interpretations: listOfServices,
          Clients: listOfClients,
          Linguists: listOfLinguists,
          Languages: listOfLanguages,
          InterpretationStatuses: listOfStatuses,
          InterpretationTypes: listOfTypes,
        };

        setOverlayData(secondData);

        setOverlayName(OVERLAY_NAMES.INTERPRETATION_NEW_EDIT);

        setContextMenu(null);
      })
      .catch((error) => {
        const message =
          error.response?.data?.message || "Failed to complete service.";
        handleErrorDialog(message, theme);
        showSnackbar(message, theme, "error");
      });
  };

  const handleContextMenuCallRequestor = () => {
    const interpretationData = listOfServices.find(
      ({ id }) => id === selectedRow,
    );

    const requestorPhones = interpretationData?.Requestor?.PhoneNumbers || [];
    const requestorPrimary = requestorPhones.find((p) => p.IsPrimary) || requestorPhones[0];
    const requestorPhone = requestorPrimary?.Number || interpretationData?.Requestor?.PhoneNumber;
    if (requestorPhone) {
      window.location.href = `tel:${requestorPhone.replace(/[^0-9+]/g, "")}`;
    }
    setContextMenu(null);
  };

  const handleContextMenuEmailRequestor = () => {
    const interpretationData = listOfServices.find(
      ({ id }) => id === selectedRow,
    );

    if (interpretationData?.Requestor?.Email) {
      const email = interpretationData.Requestor.Email;
      const subject =
        "Regarding Assignment N° " + interpretationData.ProjectNumber;
      const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(
        subject,
      )}`;
      window.open(mailtoLink, "_blank");
    }
    setContextMenu(null);
  };

  const handleContextMenuClose = () => {
    setContextMenu(null);
  };

  // Column definitions for filtering
  const filterColumns = useMemo(
    () => [
      {
        field: "ProjectNumber",
        valueGetter: (_, row) => row.ProjectNumber || "N/A",
      },
      {
        field: "Date",
        valueGetter: (_, row) =>
          row.Date ? formatDateLongNoYear(row.Date) : "N/A",
      },
      {
        field: "InterpretationStatus",
        valueGetter: (_, row) => row.InterpretationStatus || "N/A",
      },
      {
        field: "Client",
        valueGetter: (_, row) => row.Client?.Account || "N/A",
      },
      {
        field: "Requestor",
        valueGetter: (_, row) => row.Requestor?.Name || "N/A",
      },
      {
        field: "TimeStart",
        valueGetter: (_, row) => {
          const data = row.TimeStart;
          if (!data || data === "00:00:00") return "N/A";
          return dayjs
            .tz(data, "HH:mm:ss", "America/New_York")
            .format("h:mm A");
        },
      },
      {
        field: "TimeEnd",
        valueGetter: (_, row) => {
          const data = row.TimeEnd;
          if (!data || data === "00:00:00") return "N/A";
          return dayjs
            .tz(data, "HH:mm:ss", "America/New_York")
            .format("h:mm A");
        },
      },
      {
        field: "Duration",
        valueGetter: (_, row) => {
          const data = row.Duration;
          if (!data || data === "00:00:00") return "N/A";
          const [hours, minutes] = data.split(":");
          return `${hours}:${minutes}`;
        },
      },
      {
        field: "Linguist",
        valueGetter: (_, row) => row.Linguist?.Name || "N/A",
      },
      { field: "Details", valueGetter: (_, row) => row.Details || "N/A" },
    ],
    [],
  );

  const {
    columnFilters: todaysFilters,
    filteredData: todaysFilteredData,
    handleFilterChange: handleTodaysFilterChange,
    clearAllFilters: clearTodaysFilters,
    hasActiveFilters: todaysHasActiveFilters,
  } = useColumnFilters(todaysAssignments || [], filterColumns);

  // Cross-filtering data calculation for todays
  const todaysFilterData = useMemo(() => {
    return filterColumns.map((column) => {
      const otherFilters = Object.entries(todaysFilters).filter(
        ([field]) => field !== column.field,
      );

      let filteredData;
      if (otherFilters.length === 0) {
        filteredData = todaysAssignments || [];
      } else {
        filteredData = (todaysAssignments || []).filter((row) => {
          return otherFilters.every(([field, selectedValues]) => {
            if (!selectedValues || selectedValues.length === 0) return true;

            const filterColumn = filterColumns.find(
              (col) => col.field === field,
            );
            if (!filterColumn) return true;

            const value = filterColumn.valueGetter(null, row);
            return selectedValues.includes(value);
          });
        });
      }

      return filteredData;
    });
  }, [todaysFilters, todaysAssignments, filterColumns]);

  const {
    columnFilters: tomorrowsFilters,
    filteredData: tomorrowsFilteredData,
    handleFilterChange: handleTomorrowsFilterChange,
    clearAllFilters: clearTomorrowsFilters,
    hasActiveFilters: tomorrowsHasActiveFilters,
  } = useColumnFilters(tomorrowsAssignments || [], filterColumns);

  // Cross-filtering data calculation for tomorrows
  const tomorrowsFilterData = useMemo(() => {
    return filterColumns.map((column) => {
      const otherFilters = Object.entries(tomorrowsFilters).filter(
        ([field]) => field !== column.field,
      );

      let filteredData;
      if (otherFilters.length === 0) {
        filteredData = tomorrowsAssignments || [];
      } else {
        filteredData = (tomorrowsAssignments || []).filter((row) => {
          return otherFilters.every(([field, selectedValues]) => {
            if (!selectedValues || selectedValues.length === 0) return true;

            const filterColumn = filterColumns.find(
              (col) => col.field === field,
            );
            if (!filterColumn) return true;

            const value = filterColumn.valueGetter(null, row);
            return selectedValues.includes(value);
          });
        });
      }

      return filteredData;
    });
  }, [tomorrowsFilters, tomorrowsAssignments, filterColumns]);

  const {
    columnFilters: thisWeeksFilters,
    filteredData: thisWeeksFilteredData,
    handleFilterChange: handleThisWeeksFilterChange,
    clearAllFilters: clearThisWeeksFilters,
    hasActiveFilters: thisWeeksHasActiveFilters,
  } = useColumnFilters(thisWeeksAssignments || [], filterColumns);

  // Cross-filtering data calculation for this weeks
  const thisWeeksFilterData = useMemo(() => {
    return filterColumns.map((column) => {
      const otherFilters = Object.entries(thisWeeksFilters).filter(
        ([field]) => field !== column.field,
      );

      let filteredData;
      if (otherFilters.length === 0) {
        filteredData = thisWeeksAssignments || [];
      } else {
        filteredData = (thisWeeksAssignments || []).filter((row) => {
          return otherFilters.every(([field, selectedValues]) => {
            if (!selectedValues || selectedValues.length === 0) return true;

            const filterColumn = filterColumns.find(
              (col) => col.field === field,
            );
            if (!filterColumn) return true;

            const value = filterColumn.valueGetter(null, row);
            return selectedValues.includes(value);
          });
        });
      }

      return filteredData;
    });
  }, [thisWeeksFilters, thisWeeksAssignments, filterColumns]);

  const {
    columnFilters: nextWeeksFilters,
    filteredData: nextWeeksFilteredData,
    handleFilterChange: handleNextWeeksFilterChange,
    clearAllFilters: clearNextWeeksFilters,
    hasActiveFilters: nextWeeksHasActiveFilters,
  } = useColumnFilters(nextWeeksAssignments || [], filterColumns);

  // Cross-filtering data calculation for next weeks
  const nextWeeksFilterData = useMemo(() => {
    return filterColumns.map((column) => {
      const otherFilters = Object.entries(nextWeeksFilters).filter(
        ([field]) => field !== column.field,
      );

      let filteredData;
      if (otherFilters.length === 0) {
        filteredData = nextWeeksAssignments || [];
      } else {
        filteredData = (nextWeeksAssignments || []).filter((row) => {
          return otherFilters.every(([field, selectedValues]) => {
            if (!selectedValues || selectedValues.length === 0) return true;

            const filterColumn = filterColumns.find(
              (col) => col.field === field,
            );
            if (!filterColumn) return true;

            const value = filterColumn.valueGetter(null, row);
            return selectedValues.includes(value);
          });
        });
      }

      return filteredData;
    });
  }, [nextWeeksFilters, nextWeeksAssignments, filterColumns]);

  const {
    columnFilters: monthTilYesterdayFilters,
    filteredData: monthTilYesterdayFilteredData,
    handleFilterChange: handleMonthTilYesterdayFilterChange,
    clearAllFilters: clearMonthTilYesterdayFilters,
    hasActiveFilters: monthTilYesterdayHasActiveFilters,
  } = useColumnFilters(monthTilYesterdayAssignments || [], filterColumns);

  // Cross-filtering data calculation for month til yesterday
  const monthTilYesterdayFilterData = useMemo(() => {
    return filterColumns.map((column) => {
      const otherFilters = Object.entries(monthTilYesterdayFilters).filter(
        ([field]) => field !== column.field,
      );

      let filteredData;
      if (otherFilters.length === 0) {
        filteredData = monthTilYesterdayAssignments || [];
      } else {
        filteredData = (monthTilYesterdayAssignments || []).filter((row) => {
          return otherFilters.every(([field, selectedValues]) => {
            if (!selectedValues || selectedValues.length === 0) return true;

            const filterColumn = filterColumns.find(
              (col) => col.field === field,
            );
            if (!filterColumn) return true;

            const value = filterColumn.valueGetter(null, row);
            return selectedValues.includes(value);
          });
        });
      }

      return filteredData;
    });
  }, [monthTilYesterdayFilters, monthTilYesterdayAssignments, filterColumns]);

  // Check for quick filters on all grids
  const todaysHasQuickFilter = todaysFilterModel?.quickFilterValues?.length > 0;
  const todaysHasAnyActiveFilters =
    todaysHasActiveFilters || todaysHasQuickFilter;

  const tomorrowsHasQuickFilter =
    tomorrowsFilterModel?.quickFilterValues?.length > 0;
  const tomorrowsHasAnyActiveFilters =
    tomorrowsHasActiveFilters || tomorrowsHasQuickFilter;

  const thisWeeksHasQuickFilter =
    thisWeeksFilterModel?.quickFilterValues?.length > 0;
  const thisWeeksHasAnyActiveFilters =
    thisWeeksHasActiveFilters || thisWeeksHasQuickFilter;

  const nextWeeksHasQuickFilter =
    nextWeeksFilterModel?.quickFilterValues?.length > 0;
  const nextWeeksHasAnyActiveFilters =
    nextWeeksHasActiveFilters || nextWeeksHasQuickFilter;

  const monthTilYesterdayHasQuickFilter =
    monthTilYesterdayFilterModel?.quickFilterValues?.length > 0;
  const monthTilYesterdayHasAnyActiveFilters =
    monthTilYesterdayHasActiveFilters || monthTilYesterdayHasQuickFilter;

  // Compute dates using dayjs
  const todaysDate = dayjs(); //"2025-02-24");

  // Compute tomorrow's date:
  // If today is Friday (day() returns 5 with Sunday as 0) then add 3 days to get Monday.
  const tomorrowsDate =
    todaysDate.day() === 5
      ? todaysDate.add(3, "day")
      : todaysDate.add(1, "day");

  // Compute the range from the 1st day of the month until yesterday
  const monthStart = todaysDate.startOf("month");
  const yesterday = todaysDate.subtract(1, "day");
  const monthStartFormatted = monthStart.format("YYYY-MM-DD");
  const yesterdayFormatted = yesterday.format("YYYY-MM-DD");
  const monthToYesterdayRangeParams = useMemo(
    () => ({
      start: monthStartFormatted,
      end: yesterdayFormatted,
    }),
    [monthStartFormatted, yesterdayFormatted],
  );

  // Compute current week range using ISO week (Monday to Sunday)
  const currentWeekEnd = todaysDate.isoWeekday(7);

  const todayFormatted = todaysDate.format("YYYY-MM-DD");
  const currentWeekEndFormatted = currentWeekEnd.format("YYYY-MM-DD");
  const thisWeeksDateRangeParams = useMemo(
    () => ({
      start: todayFormatted,
      end: currentWeekEndFormatted,
    }),
    [todayFormatted, currentWeekEndFormatted],
  );

  // Compute next week range (Monday to Sunday)
  const nextWeekStart = todaysDate.startOf("isoWeek").add(1, "week");
  const nextWeekEnd = nextWeekStart.isoWeekday(7);

  const nextWeeksDateRange = {
    start: nextWeekStart,
    end: nextWeekEnd,
  };

  const nextWeekStartFormatted = nextWeekStart.format("YYYY-MM-DD");
  const nextWeekEndFormatted = nextWeekEnd.format("YYYY-MM-DD");
  const nextWeeksDateRangeParams = useMemo(
    () => ({
      start: nextWeekStartFormatted,
      end: nextWeekEndFormatted,
    }),
    [nextWeekStartFormatted, nextWeekEndFormatted],
  );

  const todayDay = todaysDate.date();
  const todayMonth = todaysDate.month() + 1;
  const todayYear = todaysDate.year();
  const todaysDateParams = useMemo(
    () => ({
      day: todayDay,
      month: todayMonth,
      year: todayYear,
    }),
    [todayDay, todayMonth, todayYear],
  );

  const tomorrowDay = tomorrowsDate.date();
  const tomorrowMonth = tomorrowsDate.month() + 1;
  const tomorrowYear = tomorrowsDate.year();
  const tomorrowsDateParams = useMemo(
    () => ({
      day: tomorrowDay,
      month: tomorrowMonth,
      year: tomorrowYear,
    }),
    [tomorrowDay, tomorrowMonth, tomorrowYear],
  );

  const countParams = useMemo(
    () => ({
      today: {
        day: todayDay,
        month: todayMonth,
        year: todayYear,
      },
      tomorrow: {
        day: tomorrowDay,
        month: tomorrowMonth,
        year: tomorrowYear,
      },
      thisWeek: thisWeeksDateRangeParams,
      nextWeek: nextWeeksDateRangeParams,
      monthTilYesterday: monthToYesterdayRangeParams,
    }),
    [
      todayDay,
      todayMonth,
      todayYear,
      tomorrowDay,
      tomorrowMonth,
      tomorrowYear,
      thisWeeksDateRangeParams,
      nextWeeksDateRangeParams,
      monthToYesterdayRangeParams,
    ],
  );

  // Fetch weekly interpretation data
  const fetchWeeklyData = useCallback(() => {
    // Validate required parameters
    if (!todaysDateParams || !tomorrowsDateParams || !thisWeeksDateRangeParams || 
        !nextWeeksDateRangeParams || !monthToYesterdayRangeParams || !countParams) {
      console.error("Invalid parameters for fetchWeeklyData:", { 
        todaysDateParams, tomorrowsDateParams, thisWeeksDateRangeParams,
        nextWeeksDateRangeParams, monthToYesterdayRangeParams, countParams 
      });
      handleErrorDialog("Invalid date parameters. Please try again.", theme);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    api
      .all([
        api.get("/dashboard/interpretations/weekly/day", {
          params: todaysDateParams,
        }),
        api.get("/dashboard/interpretations/weekly/day", {
          params: tomorrowsDateParams,
        }),
        api.get("/dashboard/interpretations/weekly/week", {
          params: thisWeeksDateRangeParams,
        }),
        api.get("/dashboard/interpretations/weekly/week", {
          params: nextWeeksDateRangeParams,
        }),
        api.get("/dashboard/interpretations/weekly/week", {
          params: monthToYesterdayRangeParams,
        }),
        api.get("/dashboard/interpretations/weekly/counts", {
          params: countParams,
        }),
      ])
      .then(
        api.spread(
          (
            todaysResponse,
            tomorrowsResponse,
            thisWeeksResponse,
            nextWeeksResponse,
            monthTilYesterdayResponse,
            countsResponse,
          ) => {
            try {
              // Set all data responses with validation
              if (todaysResponse?.data) {
                setTodaysAssignments(todaysResponse.data);
              } else {
                setTodaysAssignments([]);
                console.warn("No today's assignments data received");
              }
              
              if (tomorrowsResponse?.data) {
                setTomorrowsAssignments(tomorrowsResponse.data);
              } else {
                setTomorrowsAssignments([]);
                console.warn("No tomorrow's assignments data received");
              }
              
              if (thisWeeksResponse?.data) {
                setThisWeeksAssignments(thisWeeksResponse.data);
              } else {
                setThisWeeksAssignments([]);
                console.warn("No this week's assignments data received");
              }
              
              if (nextWeeksResponse?.data) {
                setNextWeeksAssignments(nextWeeksResponse.data);
              } else {
                setNextWeeksAssignments([]);
                console.warn("No next week's assignments data received");
              }
              
              if (monthTilYesterdayResponse?.data) {
                setMonthTilYesterdayAssignments(monthTilYesterdayResponse.data);
              } else {
                setMonthTilYesterdayAssignments([]);
                console.warn("No month til yesterday assignments data received");
              }
              
              if (countsResponse?.data) {
                setAssignmentsCounts(countsResponse.data);
              } else {
                setAssignmentsCounts({});
                console.warn("No counts data received");
              }
            } catch (error) {
              console.error("Error processing weekly API response:", error);
              handleErrorDialog("Error processing data. Please try again.", theme);
            } finally {
              setIsLoading(false);
            }
          },
        ),
      )
      .catch((error) => {
        console.error("Weekly dashboard API error:", error);
        const message =
          error.response?.data?.message ||
          error.message ||
          "Unable to connect to the server. Please try again later.";
        handleErrorDialog(message, theme);
        setIsLoading(false);
      });
  }, [
    countParams,
    handleErrorDialog,
    monthToYesterdayRangeParams,
    nextWeeksDateRangeParams,
    theme,
    thisWeeksDateRangeParams,
    todaysDateParams,
    tomorrowsDateParams,
  ]);

  useEffect(() => {
    fetchWeeklyData();
  }, [refreshPage, fetchWeeklyData]);

  // Listen for interpretation updates from other members
  const handleInterpretationsWeeklyRefetch = useCallback(
    (updatedBy) => {
      // Store update info for snackbar (separate effect will show it)
      setLastUpdate(updatedBy);

      // Refetch the data
      fetchWeeklyData();
    },
    [fetchWeeklyData],
  );

  // Show snackbar when update is received (in separate effect to avoid listener churn)
  useEffect(() => {
    if (lastUpdate?.userName) {
      const message = lastUpdate?.projectNumber
        ? `Assignment #${lastUpdate.projectNumber} updated by ${lastUpdate.userName}`
        : `Assignment updated by ${lastUpdate.userName}`;
      showSnackbar(message, theme, "info");
      setLastUpdate(null);
    }
  }, [lastUpdate, showSnackbar, theme]);

  useAssignmentUpdates({
    modelType: "InterpretationsList",
    recordId: "all",
    onUpdate: handleInterpretationsWeeklyRefetch,
  });

  // Fetch static data for context menu
  useEffect(() => {
    api
      .all([
        api.get("/clients/brief"),
        api.get("/linguists/brief"),
        api.get("/languages"),
        api.get("/services/interpretations/statuses"),
        api.get("/services/interpretations/types"),
      ])
      .then(
        api.spread(
          (
            clientsResponse,
            linguistsResponse,
            languagesResponse,
            statusesResponse,
            typesResponse,
          ) => {
            // Set data with validation and fallbacks
            setListOfClients(clientsResponse?.data || []);
            setListOfLinguists(linguistsResponse?.data || []);
            setListOfLanguages(languagesResponse?.data || []);
            setListOfStatuses(statusesResponse?.data || []);
            setListOfTypes(typesResponse?.data || []);
            
          },
        ),
      )
      .catch((error) => {
        console.error("Error fetching static data:", error);
        const message =
          error.response?.data?.message ||
          error.message ||
          "Failed to load required data. Some features may be limited.";
        handleErrorDialog(message, theme);
        
        // Set empty arrays as fallbacks
        setListOfClients([]);
        setListOfLinguists([]);
        setListOfLanguages([]);
        setListOfStatuses([]);
        setListOfTypes([]);
      });
  }, [handleErrorDialog, theme]);

  const dateComparator = (v1, v2) => {
    if (v1 === "N/A" && v2 === "N/A") return 0;
    if (v1 === "N/A") return 1;
    if (v2 === "N/A") return -1;
    
    // Try multiple date formats to handle different server responses
    const formats = ["M/D/YYYY", "YYYY-MM-DD", "MM/DD/YYYY", "YYYY/MM/DD"];
    let date1, date2;
    
    for (const format of formats) {
      try {
        date1 = dayjs(v1, format);
        date2 = dayjs(v2, format);
        if (date1.isValid() && date2.isValid()) {
          return date1.valueOf() - date2.valueOf();
        }
      } catch (e) {
        // Continue to next format
      }
    }
    
    // Fallback to string comparison if no valid date format found
    return String(v1).localeCompare(String(v2));
  };

  const getColumns = (
    columnFilters,
    handleFilterChange,
    filterData,
    openFilter,
    setOpenFilter,
  ) => [
    {
      field: "ProjectNumber",
      headerName: "Number",
      minWidth: 120,
      valueGetter: (_, params) => {
        const data = params.ProjectNumber;
        const value = data ? data : "N/A";
        return value;
      },
      flex: 1.2,
      renderHeader: () => (
        <DataGridHeader
          headerName="Number"
          children={
            <ColumnFilter
              field="ProjectNumber"
              data={filterData[0] || []}
              onFilterChange={(values) =>
                handleFilterChange("ProjectNumber", values)
              }
              selectedValues={columnFilters.ProjectNumber || []}
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          }
        />
      ),
    },
    {
      field: "Date",
      headerName: "Date",
      valueGetter: (_, params) => {
        const data = params.Date;
        const value = data ? formatDateLongNoYear(data) : "N/A";
        return value;
      },
      sortComparator: dateComparator,
      minWidth: 180,
      flex: 1.8,
      renderHeader: () => (
        <DataGridHeader
          headerName="Date"
          children={
            <ColumnDateFilter
              field="Date"
              data={filterData[1] || []}
              onFilterChange={(values) => handleFilterChange("Date", values)}
              selectedValues={columnFilters.Date || []}
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          }
        />
      ),
    },
    {
      field: "InterpretationStatus",
      headerName: "Status",
      minWidth: 120,
      flex: 1.2,
      renderCell: (params) => (
        <Box style={{ display: "flex", alignItems: "center", height: "100%" }}>
          <HtmlTooltip
            title={
              <Typography sx={{ whiteSpace: "pre-wrap" }}>
                {params.value}
              </Typography>
            }
          >
            <Box
              sx={{
                borderRadius: "8px",
                px: "0.5rem",
                backgroundColor: getStatusColor(theme, params.value),
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  color: theme.palette.background.default,
                }}
              >
                {params.value}
              </Typography>
            </Box>
          </HtmlTooltip>
        </Box>
      ),
      renderHeader: () => (
        <DataGridHeader
          headerName="Status"
          children={
            <ColumnFilter
              field="InterpretationStatus"
              data={filterData[2] || []}
              onFilterChange={(values) =>
                handleFilterChange("InterpretationStatus", values)
              }
              selectedValues={columnFilters.InterpretationStatus || []}
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          }
        />
      ),
    },
    {
      field: "Client",
      headerName: "Client",
      valueGetter: (_, params) => {
        const data = params.Client;
        const value = data ? data.Account : "N/A";
        return value;
      },
      minWidth: 180,
      flex: 1.8,
      renderHeader: () => (
        <DataGridHeader
          headerName="Client"
          children={
            <ColumnFilter
              field="Client"
              data={filterData[3] || []}
              onFilterChange={(values) => handleFilterChange("Client", values)}
              selectedValues={columnFilters.Client || []}
              valueGetter={(_, params) => params.Client?.Account || "N/A"}
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          }
        />
      ),
    },
    {
      field: "Requestor",
      headerName: "Requestor",
      valueGetter: (_, params) => {
        const data = params.Requestor;
        const value = data ? data.Name : "N/A";
        return value;
      },
      minWidth: 150,
      flex: 1.5,
      renderHeader: () => (
        <DataGridHeader
          headerName="Requestor"
          children={
            <ColumnFilter
              field="Requestor"
              data={filterData[4] || []}
              onFilterChange={(values) =>
                handleFilterChange("Requestor", values)
              }
              selectedValues={columnFilters.Requestor || []}
              valueGetter={(_, params) => params.Requestor?.Name || "N/A"}
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          }
        />
      ),
    },
    {
      field: "TimeStart",
      headerName: "Time Start",
      valueGetter: (_, params) => {
        const data = params.TimeStart;
        let value = "N/A";

        if (data && data !== "00:00:00") {
          value = dayjs
            .tz(data, "HH:mm:ss", "America/New_York")
            .format("h:mm A");
        }

        return value;
      },
      minWidth: 130,
      flex: 1.3,
      renderHeader: () => (
        <DataGridHeader
          headerName="Time Start"
          children={
            <ColumnFilter
              field="TimeStart"
              data={filterData[5] || []}
              onFilterChange={(values) =>
                handleFilterChange("TimeStart", values)
              }
              selectedValues={columnFilters.TimeStart || []}
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          }
        />
      ),
    },
    {
      field: "TimeEnd",
      headerName: "Time End",
      valueGetter: (_, params) => {
        const data = params.TimeEnd;
        let value = "N/A";

        if (data && data !== "00:00:00") {
          value = dayjs
            .tz(data, "HH:mm:ss", "America/New_York")
            .format("h:mm A");
        }

        return value;
      },
      minWidth: 130,
      flex: 1.3,
      renderHeader: () => (
        <DataGridHeader
          headerName="Time End"
          children={
            <ColumnFilter
              field="TimeEnd"
              data={filterData[6] || []}
              onFilterChange={(values) => handleFilterChange("TimeEnd", values)}
              selectedValues={columnFilters.TimeEnd || []}
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          }
        />
      ),
    },
    {
      field: "Duration",
      headerName: "Duration",
      valueGetter: (_, params) => {
        const data = params.Duration;
        let value = "N/A";

        if (data && data !== "00:00:00") {
          const [hours, minutes] = data.split(":");
          value = `${hours}:${minutes}`;
        }

        return value;
      },
      minWidth: 130,
      flex: 1.3,
      renderHeader: () => (
        <DataGridHeader
          headerName="Duration"
          children={
            <ColumnFilter
              field="Duration"
              data={filterData[7] || []}
              onFilterChange={(values) =>
                handleFilterChange("Duration", values)
              }
              selectedValues={columnFilters.Duration || []}
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          }
        />
      ),
    },
    {
      field: "Linguist",
      headerName: "Linguist",
      valueGetter: (_, params) => {
        const data = params.Linguist;
        const value = data ? data.Name : "N/A";
        return value;
      },
      minWidth: 140,
      flex: 1.4,
      renderHeader: () => (
        <DataGridHeader
          headerName="Linguist"
          children={
            <ColumnFilter
              field="Linguist"
              data={filterData[8] || []}
              onFilterChange={(values) =>
                handleFilterChange("Linguist", values)
              }
              selectedValues={columnFilters.Linguist || []}
              valueGetter={(_, params) => params.Linguist?.Name || "N/A"}
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          }
        />
      ),
    },
    {
      field: "Details",
      headerName: "Details",
      valueGetter: (_, params) => {
        const data = params.Details;
        const value = data ? data : "N/A";
        return value;
      },
      renderCell: (params) => (
        <Box style={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography
            sx={{
              display: "-webkit-box",
              overflow: "hidden",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
            }}
            style={{ whiteSpace: "pre-wrap" }}
          >
            {params.value}
          </Typography>
        </Box>
      ),
      minWidth: 450,
      flex: 4.5,
      renderHeader: () => (
        <DataGridHeader
          headerName="Details"
          children={
            <ColumnFilter
              field="Details"
              data={filterData[9] || []}
              onFilterChange={(values) => handleFilterChange("Details", values)}
              selectedValues={columnFilters.Details || []}
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          }
        />
      ),
    },
  ];

  const CustomToolbar = useMemo(() => {
    const Toolbar = ({ hasAnyActiveFilters, onClear, title }) => {
      return (
        <GridToolbarContainer
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          {title && (
            <Typography
              variant="h5"
              sx={{
                fontWeight: "bold",
                color: theme.palette.primary.text2,
                margin: 0,
                padding: 0,
                alignSelf: "flex-start",
              }}
            >
              {title}
            </Typography>
          )}

          <Box
            sx={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              alignItems: "center",
            }}
          >
            {hasAnyActiveFilters && (
              <Button
                startIcon={<ClearIcon />}
                onClick={onClear}
                variant="outlined"
                size="small"
                sx={{
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  "&:hover": {
                    borderColor: theme.palette.primary.buttonHoverSelected,
                    backgroundColor: theme.palette.primary.buttonHoverSelected,
                    color: theme.palette.background.default,
                  },
                }}
              >
                Clear Filters
              </Button>
            )}
          </Box>

          <Box sx={{ flex: 1 }} />

          <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
            <CEQuickFilter />
          </Box>
        </GridToolbarContainer>
      );
    };

    return Toolbar;
  }, [
    theme.palette.background.default,
    theme.palette.primary.buttonHoverSelected,
    theme.palette.primary.main,
    theme.palette.primary.text2,
  ]);

  const validateAssignmentData = (assignments) => {
    if (!Array.isArray(assignments)) {
      console.warn("Invalid assignments data: expected array, got", typeof assignments);
      return [];
    }
    
    return assignments.map(assignment => {
      if (!assignment || typeof assignment !== 'object') {
        console.warn("Invalid assignment entry:", assignment);
        return null;
      }
      
      // Ensure required fields exist with fallbacks
      return {
        id: assignment.id || null,
        ProjectNumber: assignment.ProjectNumber || "N/A",
        Date: assignment.Date || null,
        TimeStart: assignment.TimeStart || null,
        TimeEnd: assignment.TimeEnd || null,
        Duration: assignment.Duration || null,
        Details: assignment.Details || "",
        InterpretationStatus: assignment.InterpretationStatus || "Unknown",
        ServiceCategory: assignment.ServiceCategory || "Interpretation",
        Client: assignment.Client || null,
        Requestor: assignment.Requestor || null,
        Linguist: assignment.Linguist || null,
        ...assignment, // Include any additional fields
      };
    }).filter(Boolean); // Remove null entries
  };

  // Validate assignments data before setting state
  const validatedTodaysAssignments = useMemo(() => {
    return validateAssignmentData(todaysAssignments);
  }, [todaysAssignments]);

  const validatedTomorrowsAssignments = useMemo(() => {
    return validateAssignmentData(tomorrowsAssignments);
  }, [tomorrowsAssignments]);

  const validatedThisWeeksAssignments = useMemo(() => {
    return validateAssignmentData(thisWeeksAssignments);
  }, [thisWeeksAssignments]);

  const validatedNextWeeksAssignments = useMemo(() => {
    return validateAssignmentData(nextWeeksAssignments);
  }, [nextWeeksAssignments]);

  const validatedMonthTilYesterdayAssignments = useMemo(() => {
    return validateAssignmentData(monthTilYesterdayAssignments);
  }, [monthTilYesterdayAssignments]);

  const getDataGrid = (
    title,
    listOfServices,
    filteredData,
    columnFilters,
    handleFilterChange,
    clearAllFilters,
    hasAnyActiveFilters,
    openFilter,
    setOpenFilter,
    filterData,
    filterModel,
    onFilterModelChange,
  ) => {
    return (
      <>
        <DataGrid
          loading={isLoading}
          initialState={{
            pagination: { paginationModel: { pageSize: 5 } },
          }}
          pageSizeOptions={[5, 10, 25, { value: -1, label: "All" }]}
          getRowId={(row) => {
            return row.id;
          }}
          rows={filteredData || []}
          columns={getColumns(
            columnFilters,
            handleFilterChange,
            filterData || [],
            openFilter,
            setOpenFilter,
          )}
          filterModel={filterModel}
          onFilterModelChange={onFilterModelChange}
          rowSelectionModel={
            openFilter ||
            openTodaysFilter ||
            openTomorrowsFilter ||
            openThisWeeksFilter ||
            openNextWeeksFilter ||
            openMonthTilYesterdayFilter
              ? []
              : undefined
          }
          disableRowSelectionOnClick={
            !!(
              openFilter ||
              openTodaysFilter ||
              openTomorrowsFilter ||
              openThisWeeksFilter ||
              openNextWeeksFilter ||
              openMonthTilYesterdayFilter
            )
          }
          onRowClick={(params, event, details) => {
            if (openFilter) {
              setOpenFilter(null);
              return;
            }

            // Close all other filters
            if (openTodaysFilter) {
              handleSetOpenTodaysFilter(null);
              return;
            }
            if (openTomorrowsFilter) {
              handleSetOpenTomorrowsFilter(null);
              return;
            }
            if (openThisWeeksFilter) {
              handleSetOpenThisWeeksFilter(null);
              return;
            }
            if (openNextWeeksFilter) {
              handleSetOpenNextWeeksFilter(null);
              return;
            }
            if (openMonthTilYesterdayFilter) {
              handleSetOpenMonthTilYesterdayFilter(null);
              return;
            }

            //to do
            const data = {
              ComingFrom: "/dashboard",
              Interpretation: params.row,
              InterpretationID: params.row.id,
            };

            setOverlayData(data);

            navigate(`/interpretations/${params.row.id}`, { state: data });
          }}
          getCellClassName={(params) => {
            return params.value === "✘" ||
              params.value === "N/A" ||
              params.value === "0%" ||
              params.value === "null"
              ? "notAvailable"
              : "";
          }}
          slots={{
            toolbar: CustomToolbar,
          }}
          slotProps={{
            row: {
              onContextMenu: handleContextMenu,
              style: { cursor: "context-menu" },
            },
            loadingOverlay: {
              variant: "skeleton",
              noRowsVariant: "skeleton",
            },
            toolbar: {
              hasAnyActiveFilters,
              onClear: clearAllFilters,
              title,
            },
          }}
          disableColumnMenu
          disableColumnSorting
          ignoreDiacritics
          sx={{
            backgroundColor: theme.palette.background.default,
            borderRadius: "0px",
            boxShadow: "none",
            height: "100%",
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: "bold",
              color: theme.palette.primary.main,
            },
            "& .MuiDataGrid-scrollbarFiller": {
              backgroundColor: theme.palette.background.alt,
            },
            "& .MuiDataGrid-menuIconButton": {
              display: "none !important",
            },
            "& .MuiDataGrid-columnHeader .MuiDataGrid-menuIcon": {
              display: "none !important",
            },
            "& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer": {
              display: "none !important",
            },
            "& .MuiDataGrid-columnSeparator": {
              color: theme.palette.primary.main,
            },
            "& .MuiButton-root": {
              color: theme.palette.primary.main,
            },
            "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
              outline: "none !important",
            },
            "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus":
              {
                outline: "none !important",
              },
            "& .MuiDataGrid-scrollbar": {
              margin: "0 0 0 0",
            },
            "& .MuiDataGrid-columnHeaderTitleContainerContent": {
              flex: 1,
            },
            "& .MuiTypography-root.MuiTypography-body1": {
              textAlign: "left !important",
            },
            "& .MuiDataGrid-toolbarContainer .MuiButton-root[aria-label*='Export']":
              {
                display: "none !important",
              },
          }}
        />
      </>
    );
  };

  return (
    <Grid container spacing={2} size={{ xs: 12 }}>
      <Grid container spacing={2} size={{ xs: 12 }} sx={{ px: 1, pt: 1 }}>
        <Grid size={{ xs: 12, sm: 12 / 5 }}>
          <CardDashboardText
            title="Today"
            content={assignmentsCounts?.today || (isLoading ? "" : "0")}
            isLoading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 12 / 5 }}>
          <CardDashboardText
            title="Tomorrow"
            content={assignmentsCounts?.tomorrow || (isLoading ? "" : "0")}
            isLoading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 12 / 5 }}>
          <CardDashboardText
            title="This Week"
            content={assignmentsCounts?.thisWeek || (isLoading ? "" : "0")}
            isLoading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 12 / 5 }}>
          <CardDashboardText
            title="Next Week"
            content={assignmentsCounts?.nextWeek || (isLoading ? "" : "0")}
            isLoading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 12 / 5 }}>
          <CardDashboardText
            title="So Far This Month"
            content={
              assignmentsCounts?.monthTilYesterday || (isLoading ? "" : "0")
            }
            isLoading={isLoading}
          />
        </Grid>
      </Grid>

      {/* Second Row: Two Grids with DataGrid components  */}
      <Grid container spacing={2} size={{ xs: 12 }} sx={{ px: 1, pb: 1 }}>
        <Grid size={{ xs: 12 }} sx={{ display: "flex", minHeight: 0 }}>
          <CardDashboardItem
            height="100%"
            title={null}
            content={
              // Check if todaysDate is Saturday (6) or Sunday (0)
              todaysDate.day() === 6 || todaysDate.day() === 0
                ? "Today is the weekend, go rest!"
                : todaysAssignments?.length > 0 || isLoading
                  ? getDataGrid(
                      assignmentsCounts?.today +
                        " Assignments Today: " +
                        formatDateLongNoYear(todaysDate),
                      todaysAssignments,
                      todaysFilteredData,
                      todaysFilters,
                      handleTodaysFilterChange,
                      handleClearTodaysFilters,
                      todaysHasAnyActiveFilters,
                      openTodaysFilter,
                      handleSetOpenTodaysFilter,
                      todaysFilterData,
                      todaysFilterModel,
                      setTodaysFilterModel,
                    )
                  : "No Assignments Today that are Requested/Confirmed."
            }
            isLoading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 12 }} sx={{ display: "flex", minHeight: 0 }}>
          <CardDashboardItem
            height="100%"
            title={null}
            content={
              tomorrowsAssignments?.length > 0 || isLoading
                ? getDataGrid(
                    assignmentsCounts?.tomorrow +
                      " Assignments Tomorrow: " +
                      formatDateLongNoYear(tomorrowsDate),
                    tomorrowsAssignments,
                    tomorrowsFilteredData,
                    tomorrowsFilters,
                    handleTomorrowsFilterChange,
                    handleClearTomorrowsFilters,
                    tomorrowsHasAnyActiveFilters,
                    openTomorrowsFilter,
                    handleSetOpenTomorrowsFilter,
                    tomorrowsFilterData,
                    tomorrowsFilterModel,
                    setTomorrowsFilterModel,
                  )
                : "No Assignments Tomorrow that are Requested/Confirmed."
            }
            isLoading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 12 }} sx={{ display: "flex", minHeight: 0 }}>
          <CardDashboardItem
            height="100%"
            title={null}
            content={
              thisWeeksAssignments?.length > 0 || isLoading
                ? getDataGrid(
                    assignmentsCounts?.thisWeek +
                      " Assignments This Week: " +
                      formatDateLongNoYear(thisWeeksDateRangeParams.start) +
                      " > " +
                      formatDateLongNoYear(thisWeeksDateRangeParams.end),
                    thisWeeksAssignments,
                    thisWeeksFilteredData,
                    thisWeeksFilters,
                    handleThisWeeksFilterChange,
                    handleClearThisWeeksFilters,
                    thisWeeksHasAnyActiveFilters,
                    openThisWeeksFilter,
                    handleSetOpenThisWeeksFilter,
                    thisWeeksFilterData,
                    thisWeeksFilterModel,
                    setThisWeeksFilterModel,
                  )
                : "No Assignments This Week that are Requested/Confirmed."
            }
            isLoading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 12 }} sx={{ display: "flex", minHeight: 0 }}>
          <CardDashboardItem
            height="100%"
            title={null}
            content={
              nextWeeksAssignments?.length > 0 || isLoading
                ? getDataGrid(
                    assignmentsCounts?.nextWeek +
                      " Assignments Next Week (ISO): " +
                      formatDateLongNoYear(nextWeeksDateRange.start) +
                      " > " +
                      formatDateLongNoYear(nextWeeksDateRange.end),
                    nextWeeksAssignments,
                    nextWeeksFilteredData,
                    nextWeeksFilters,
                    handleNextWeeksFilterChange,
                    handleClearNextWeeksFilters,
                    nextWeeksHasAnyActiveFilters,
                    openNextWeeksFilter,
                    handleSetOpenNextWeeksFilter,
                    nextWeeksFilterData,
                    nextWeeksFilterModel,
                    setNextWeeksFilterModel,
                  )
                : "No Assignments Next Week that are Requested/Confirmed."
            }
            isLoading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Grid size={{ xs: 12 }} sx={{ display: "flex", minHeight: 0 }}>
            <CardDashboardItem
              height="100%"
              title={null}
              content={
                monthTilYesterdayAssignments?.length > 0 || isLoading
                  ? getDataGrid(
                      assignmentsCounts?.monthTilYesterday +
                        " Assignments This Month so far: " +
                        formatDateLongNoYear(
                          monthToYesterdayRangeParams.start,
                        ) +
                        " > " +
                        formatDateLongNoYear(monthToYesterdayRangeParams.end),
                      monthTilYesterdayAssignments,
                      monthTilYesterdayFilteredData,
                      monthTilYesterdayFilters,
                      handleMonthTilYesterdayFilterChange,
                      handleClearMonthTilYesterdayFilters,
                      monthTilYesterdayHasAnyActiveFilters,
                      openMonthTilYesterdayFilter,
                      handleSetOpenMonthTilYesterdayFilter,
                      monthTilYesterdayFilterData,
                      monthTilYesterdayFilterModel,
                      setMonthTilYesterdayFilterModel,
                    )
                  : "No Assignments From the start of this month until yesterday that are Requested/Confirmed."
              }
              isLoading={isLoading}
            />
          </Grid>
        </Grid>
      </Grid>

      <Menu
        open={contextMenu !== null}
        onClose={handleContextMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        slotProps={{
          paper: {
            sx: {
              bgcolor: theme.palette.background.alt,
              border: `1px solid ${theme.palette.divider}`,
              "& .MuiListItemIcon-root": {
                color: theme.palette.primary.main,
              },
            },
          },
        }}
      >
        <MenuItem onClick={handleContextMenuEdit}>
          <ListItemIcon>
            <EditRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText sx={{ color: theme.palette.primary.text }}>
            Edit
          </ListItemText>
        </MenuItem>

        <MenuItem onClick={handleContextMenuComplete}>
          <ListItemIcon>
            <TaskRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText sx={{ color: theme.palette.primary.text }}>
            Complete
          </ListItemText>
        </MenuItem>

        <MenuItem onClick={handleContextMenuAddTask}>
          <ListItemIcon>
            <AddTaskRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText sx={{ color: theme.palette.primary.text }}>
            Add Task
          </ListItemText>
        </MenuItem>

        <Divider sx={{ my: 0.5 }} />

        <MenuItem onClick={handleContextMenuCallRequestor}>
          <ListItemIcon>
            <CallRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText sx={{ color: theme.palette.primary.text }}>
            Call{" "}
            {listOfServices?.find(({ id }) => id === selectedRow)?.Requestor
              ?.Name || "Requestor"}
          </ListItemText>
        </MenuItem>

        <MenuItem onClick={handleContextMenuEmailRequestor}>
          <ListItemIcon>
            <EmailRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText sx={{ color: theme.palette.primary.text }}>
            Email{" "}
            {listOfServices?.find(({ id }) => id === selectedRow)?.Requestor
              ?.Name || "Requestor"}
          </ListItemText>
        </MenuItem>
      </Menu>
    </Grid>
  );
};

export default DashboardInterpretationsWeekly;
